import { startViewTransition } from '@theme/utilities';

function wallpaperRows(cards, columns) {
  const remaining = [...cards];
  const rows = [];
  while (remaining.length) {
    const format = remaining[0].dataset.wallpaperKind;
    const row = [];
    for (let index = 0; index < remaining.length && row.length < columns[format];) {
      if (remaining[index].dataset.wallpaperKind === format) row.push(...remaining.splice(index, 1));
      else index++;
    }
    rows.push({ format, cards: row });
  }
  return rows;
}

class WallpaperDensity extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    window.addEventListener('resize', this.syncToggle);
    this.syncToggle();
    requestAnimationFrame(() => this.fillChronologicalRows());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    window.removeEventListener('resize', this.syncToggle);
  }

  choices(format, mobile) {
    return mobile ? ['1', '2'] : format === 'desktop-wallpapers' ? ['2', '3', '4'] : ['4', '6'];
  }

  currentColumns(catalog, mobile) {
    const choices = this.choices(catalog.dataset.wallpaperFormat, mobile);
    const value = catalog.dataset[mobile ? 'mobileColumns' : 'desktopColumns'];
    return choices.includes(value) ? value : mobile ? (catalog.dataset.wallpaperFormat === 'desktop-wallpapers' ? '1' : '2') : (catalog.dataset.wallpaperFormat === 'desktop-wallpapers' ? '3' : '4');
  }

  syncToggle = () => {
    const catalog = this.closest('.wallpaper-catalog');
    const button = this.querySelector('[data-grid-toggle]');
    if (!catalog || !button) return;
    for (const icon of button.querySelectorAll('[data-layout-icon]')) {
      icon.hidden = icon.dataset.columns !== this.currentColumns(catalog, icon.dataset.viewport === 'mobile');
    }
    const mobile = window.innerWidth < 750;
    const choices = this.choices(catalog.dataset.wallpaperFormat, mobile);
    const next = choices[(choices.indexOf(this.currentColumns(catalog, mobile)) + 1) % choices.length];
    button.setAttribute('aria-label', button.dataset.labelFormat.replace('[count]', next));
    this.packChronologicalRows(catalog, catalog.querySelector('.product-grid'));
  };

  onClick = async (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('[data-wallpaper-shuffle]')) {
      this.shuffleCatalog();
      return;
    }
    const button = event.target.closest('[data-grid-toggle]');
    if (!button || this.changingView) return;
    const catalog = this.closest('.wallpaper-catalog');
    if (!catalog) return;
    const mobile = window.innerWidth < 750;
    const choices = this.choices(catalog.dataset.wallpaperFormat, mobile);
    const next = choices[(choices.indexOf(this.currentColumns(catalog, mobile)) + 1) % choices.length];
    this.changingView = true;
    try {
      await startViewTransition(() => {
        catalog.dataset[mobile ? 'mobileColumns' : 'desktopColumns'] = next;
        this.syncToggle();
      }, ['product-grid']);
    } finally {
      this.changingView = false;
    }
  };

  async shuffleCatalog() {
    const button = this.querySelector('[data-wallpaper-shuffle]');
    if (button.disabled) return;
    const catalog = this.closest('.wallpaper-catalog');
    const grid = catalog?.querySelector('.product-grid');
    if (!grid) return;
    const error = this.querySelector('[data-wallpaper-shuffle-error]');
    error.hidden = true;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    try {
      const rows = await this.loadCatalogRows(catalog, grid);
      const lastPage = Number(grid.dataset.lastPage);
      if (!this.isConnected) return;
      catalog.infinityScrollObserver?.disconnect();
      for (const row of rows) row.dataset.page = '1';
      grid.dataset.lastPage = '1';
      if (lastPage > 1) grid.replaceChildren(...rows);
      this.randomizeGrid(grid);
      if (this.chronologicalCards) {
        this.chronologicalCards = [...grid.querySelectorAll(':scope > .wallpaper-row')].flatMap((row) => [...row.children]);
        this.rowColumns = null;
        this.packChronologicalRows(catalog, grid);
      }
    } catch {
      error.hidden = false;
    } finally {
      button.disabled = false;
      button.removeAttribute('aria-busy');
    }
  }

  // shortcut: all catalog cards are loaded for row packing; use buffered lookahead if larger catalogs become slow.
  async fillChronologicalRows() {
    const catalog = this.closest('.wallpaper-catalog');
    const grid = catalog?.querySelector('.product-grid');
    if (!grid || catalog.dataset.wallpaperFormat !== 'all-wallpapers' || !['created-descending', 'created-ascending'].includes(catalog.dataset.wallpaperSort)) return;
    const button = this.querySelector('[data-wallpaper-shuffle]');
    button.disabled = true;
    grid.setAttribute('aria-busy', 'true');
    catalog.infinityScrollObserver?.disconnect();
    try {
      const rows = await this.loadCatalogRows(catalog, grid);
      const cards = rows.flatMap((row) => [...row.children]);
      const ranks = cards.map((card) => Number(card.dataset.wallpaperOrder));
      if (cards.some((card) => !['mobile', 'desktop'].includes(card.dataset.wallpaperKind)) || ranks.some((rank) => !Number.isFinite(rank)) || new Set(ranks).size !== cards.length) throw new Error('Missing wallpaper sort order');
      if (!this.isConnected) return;
      this.chronologicalCards = cards.sort((a, b) => Number(a.dataset.wallpaperOrder) - Number(b.dataset.wallpaperOrder));
      catalog.infinityScrollObserver?.disconnect();
      this.packChronologicalRows(catalog, grid);
      grid.dataset.lastPage = '1';
    } catch (error) {
      console.warn('Keeping paginated wallpaper rows:', error);
      if (this.isConnected) {
        for (const marker of catalog.querySelectorAll('[ref="viewMoreNext"], [ref="viewMorePrevious"]')) catalog.infinityScrollObserver?.observe(marker);
      }
    } finally {
      grid.removeAttribute('aria-busy');
      button.disabled = false;
    }
  }

  packChronologicalRows(catalog, grid) {
    if (!this.chronologicalCards) return;
    const mobile = window.innerWidth < 750;
    const columns = { mobile: Number(this.currentColumns(catalog, mobile)), desktop: mobile ? 1 : 3 };
    const key = `${columns.mobile}:${columns.desktop}`;
    if (this.rowColumns === key) return;
    const previousRows = [...grid.querySelectorAll(':scope > .wallpaper-row')];
    const rows = wallpaperRows(this.chronologicalCards, columns).map(({ format, cards }, index) => {
      const row = previousRows[index] || document.createElement('li');
      row.className = `product-grid__item wallpaper-row wallpaper-row--${format}`;
      row.dataset.page = '1';
      row.setAttribute('ref', 'cards[]');
      if (!row.isConnected) grid.append(row);
      for (const card of cards) {
        if (row.moveBefore && card.isConnected && card.ownerDocument === row.ownerDocument) row.moveBefore(card, null);
        else row.append(card);
      }
      return row;
    });
    const retained = new Set(rows);
    for (const child of [...grid.children]) if (!retained.has(child)) child.remove();
    this.rowColumns = key;
  }

  async loadCatalogRows(catalog, grid) {
    const rows = [...grid.querySelectorAll(':scope > [data-page]')];
    const loaded = new Set(rows.map((row) => Number(row.dataset.page)));
    const lastPage = Number(grid.dataset.lastPage);
    if (lastPage > 1) {
      const { sectionRenderer } = await import('@theme/section-renderer');
      for (let page = 1; page <= lastPage; page++) {
        if (loaded.has(page)) continue;
        const url = new URL(window.location.href);
        url.searchParams.set('page', String(page));
        const html = await sectionRenderer.getSectionHTML(catalog.sectionId, true, url);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = [...parsed.querySelectorAll('.product-grid > [data-page]')];
        if (!incoming.length || incoming.some((row) => Number(row.dataset.page) !== page)) throw new Error('Missing wallpaper page');
        rows.push(...incoming);
      }
    }
    const cards = rows.flatMap((row) => row.classList.contains('wallpaper-row') ? [...row.children] : [row]);
    const ids = new Set(cards.map((card) => card.dataset.productId));
    if (cards.length !== Number(this.dataset.productCount) || ids.size !== cards.length || ids.has(undefined)) throw new Error('Incomplete wallpaper catalog');
    return rows;
  }

  shuffle(nodes) {
    const original = [...nodes];
    for (let index = nodes.length - 1; index > 0; index--) {
      const other = Math.floor(Math.random() * (index + 1));
      [nodes[index], nodes[other]] = [nodes[other], nodes[index]];
    }
    if (nodes.length > 1 && nodes.every((node, index) => node === original[index])) nodes.push(nodes.shift());
    return nodes;
  }

  randomizeGrid(grid) {
    const rows = [...grid.querySelectorAll(':scope > [data-page]')];
    for (const format of ['mobile', 'desktop']) {
      const group = rows.filter((row) => row.classList.contains(`wallpaper-row--${format}`));
      const cards = this.shuffle(group.flatMap((row) => [...row.children]));
      const sizes = group.map((row) => row.children.length);
      group.forEach((row, index) => {
        for (const card of cards.splice(0, sizes[index])) {
          if (row.moveBefore) row.moveBefore(card, null);
          else row.append(card);
        }
      });
    }
    for (const row of this.shuffle(rows)) {
      if (grid.moveBefore) grid.moveBefore(row, null);
      else grid.append(row);
    }
  }

}

if (!customElements.get('wallpaper-density')) customElements.define('wallpaper-density', WallpaperDensity);
